import LoadingChart from '@/app/modules/apps/dashboard/components/LoadingChart'
import ApexCharts, { ApexOptions } from 'apexcharts'
import clsx from 'clsx'
import React, { useEffect, useMemo, useRef } from 'react'
import { FormattedNumber, IntlProvider } from 'react-intl'
import { getCSS, getCSSVariableValue } from '../../../assets/ts/_utils'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'

type Props = {
  className: string
  color: string
  secondColor?: string
  title?: string
  subtitle?: string
  series?: any
  categories?: any
  height?: string
  width?: string
  total?: number
  isCurrency?: boolean
  isTotal?: boolean
  data?: any[]
  headers?: any[]
  percentages?: any[]
  formatCurrency?: any[]
  formatThousand?: any[]
  layout?: 'vertical' | 'horizontal'
  isLoading?: boolean
  tableView?: boolean
}

function numberWithCommas(x) {
  return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, '.')
}

const BarChartOnly: React.FC<Props> = ({
  color = 'primary',
  secondColor,
  height = '300px',
  series = [],
  categories = [],
  isCurrency = true,
  data = [],
  headers = [],
  percentages = [],
  formatCurrency = [],
  formatThousand = [],
  layout = 'vertical',
  isLoading = false,
  tableView = true
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, series])

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(height, color, secondColor, series, categories, isCurrency, percentages)
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  const columnLayout = useMemo(
    () => clsx({ col: layout === 'horizontal', 'col-12': layout === 'vertical' }),
    [layout]
  )

  return (
    <>
      <div className='card-body'>
        <div className='row'>
          <div className={columnLayout}>
            {isLoading && <LoadingChart />}
            {data === null && <label className='form-label'>no result data</label>}
            {!isLoading && (
              <div ref={chartRef} id='kt_charts_widget_1_chart' style={{ height: height }} />
            )}
          </div>
          {tableView && data && headers && (
            <div className={columnLayout}>
              <div className='table-responsive mt-5 px-5'>
                {/* begin::Table */}
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  {/* begin::Table head */}
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-30px'>No</th>
                      {headers.map((x) => (
                        <th className='min-w-30px' key={x}>
                          {x}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  {/* end::Table head */}
                  {/* begin::Table body */}
                  <tbody className='text-dark mb-1 fs-6'>
                    {data && data?.length > 0 ? (
                      data?.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <a
                              href='#'
                              className='text-dark fw-semibold text-hover-primary d-block fs-6'
                            >
                              {index + 1}
                            </a>
                          </td>
                          {Object.keys(item).map((key) => {
                            let useFormatCurrency = false
                            if (formatCurrency.includes(key)) {
                              useFormatCurrency = true
                            }
                            let useFormatThousand = false
                            if (formatThousand.includes(key)) {
                              useFormatThousand = true
                            }
                            return (
                              <td key={item[key]}>
                                <div className='d-flex align-items-center'>
                                  <div className='d-flex justify-content-start flex-column'>
                                    <span className='text-dark fw-bold fs-6'>
                                      {useFormatCurrency && (
                                        <>
                                          Rp.{' '}
                                          <IntlProvider locale='id'>
                                            <FormattedNumber
                                              value={item[key] || 0}
                                              maximumFractionDigits={2}
                                            />
                                          </IntlProvider>{' '}
                                        </>
                                      )}
                                      {useFormatThousand && (
                                        <>
                                          <IntlProvider locale='id'>
                                            <FormattedNumber
                                              value={item[key] || 0}
                                              maximumFractionDigits={2}
                                            />
                                          </IntlProvider>{' '}
                                        </>
                                      )}
                                      {!useFormatCurrency && !useFormatThousand && item[key]}
                                    </span>
                                  </div>
                                </div>
                              </td>
                            )
                          })}
                          {/* <td className='text-end'>
                      <div className='d-flex flex-column w-100 me-2'>
                        <div className='d-flex flex-stack mb-2'>
                          <span className='text-dark me-2 fs-7 fw-semibold'>
                            {item.total_transaction || 0}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className='text-end'>
                      <div className='d-flex flex-column w-100 me-2'>
                        <div className='d-flex flex-stack mb-2'>
                          <span className='text-dark me-2 fs-7 fw-semibold'>
                            Rp.{' '}
                            <IntlProvider locale='id'>
                              <FormattedNumber value={item.total_sales || 0} />
                            </IntlProvider>{' '}
                          </span>
                        </div>
                      </div>
                    </td> */}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={headers.length + 1}>
                          <div className='w-100 text-center'>Belum ada data</div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {/* end::Table body */}
                </table>
                {/* end::Table */}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export { BarChartOnly }

function getChartOptions(
  height: number,
  color: string,
  secondColor: string | undefined,
  series: any,
  categories,
  isCurrency: boolean,
  percentages
): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue(`--bs-${color}`)
  const secondaryColor = secondColor
    ? getCSSVariableValue(`--bs-${secondColor}`)
    : getCSSVariableValue(`--bs-gray-300`)

  return {
    series: series,
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height: height,
      // width: series?.length * 1000,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '30%',
        borderRadius: 5,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: categories,
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function (val) {
          return isCurrency ? 'Rp. ' + numberWithCommas(val) : '' + numberWithCommas(val)
        },
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val, { seriesIndex, dataPointIndex }) {
          if (percentages.length > 0) {
            const percentage = percentages[dataPointIndex]

            let valuePercentage = -1

            Object.keys(percentage).forEach((key, index) => {
              if (index === seriesIndex) {
                valuePercentage = percentage[key]
              }
            })

            if (valuePercentage !== -1) {
              return `${val} (${valuePercentage}%)`
            }
          }

          return isCurrency ? 'Rp. ' + numberWithCommas(val) : '' + numberWithCommas(val)
        },
      },
    },
    colors: [baseColor, secondaryColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}
